/**
 * The selection a bound `texteditor` shares with the app (docs/ops-protocol.md,
 * "Rich text editors"), the same for the rich editor (src/richEditor.ts) and
 * the plain textarea (src/plainEditor.ts): what the editor last reported, or
 * was last moved to, so a selection coming back from the app is not applied
 * again and the viewer's is not echoed; and one the app set while the editor
 * was not focused, kept for when the app focuses it.
 */

import type { TextSelectionRange } from './protocol';

/** The selection each bound editor last reported, or was last moved to. */
const reported = new WeakMap<HTMLElement, string>();
/** The text each editor last reported (what the viewer typed): a `text` coming back equal to it is an echo, any other came from outside. */
const reportedText = new WeakMap<HTMLElement, string>();
/** A selection the app set while the editor was not focused: where its caret goes when the app focuses it. */
const pending = new WeakMap<HTMLElement, TextSelectionRange>();

const key = (range: TextSelectionRange): string => `${range.start}:${range.end}`;

/** Whether the editor's `selection` is bound (the prop is in its props): only then is the viewer's reported. */
export function markSelectionBound(el: HTMLElement, bound: boolean): void {
  if (bound) el.dataset['sbSelection'] = '';
  else delete el.dataset['sbSelection'];
}

export function isSelectionBound(el: HTMLElement): boolean {
  return el.dataset['sbSelection'] !== undefined;
}

/**
 * Whether the field or editor has `deleteBackward` in its props: Backspace
 * with nothing selected and the caret at offset 0 then sends `deletebackward`
 * and deletes nothing (docs/ops-protocol.md, "The delete key at the start of
 * a field"); the same mark for a `textfield`, a plain editor and a rich one.
 */
export function markDeleteBackward(el: HTMLElement, wanted: boolean): void {
  if (wanted) el.dataset['sbDeleteBackward'] = '';
  else delete el.dataset['sbDeleteBackward'];
}

export function wantsDeleteBackward(el: HTMLElement): boolean {
  return el.dataset['sbDeleteBackward'] !== undefined;
}

/** The editor reported `text` (the viewer typed it). */
export function noteReportedText(el: HTMLElement, text: string): void {
  reportedText.set(el, text);
}

/**
 * Where a `text` the app sends came from: the echo of what the editor
 * reported, or outside (another person's edit, the app's own change). The
 * caret survives an echo on its own; an outside change moves what is under
 * it, so the caret is moved too (`shiftOffset`), or the app's selection
 * re-applied even when its numbers did not change.
 */
export function textArrived(el: HTMLElement, text: string): 'echo' | 'remote' {
  return reportedText.get(el) === text ? 'echo' : 'remote';
}

/**
 * Where an offset into `before` is in `after`: the same offset before the
 * change (the common prefix), moved by the change's length after it (the
 * common suffix), and at the change's end inside it.
 */
export function shiftOffset(before: string, after: string, offset: number): number {
  const shortest = Math.min(before.length, after.length);
  let prefix = 0;
  while (prefix < shortest && before.charCodeAt(prefix) === after.charCodeAt(prefix)) prefix += 1;
  let suffix = 0;
  while (suffix < shortest - prefix && before.charCodeAt(before.length - 1 - suffix) === after.charCodeAt(after.length - 1 - suffix)) suffix += 1;
  if (offset <= prefix) return offset;
  if (offset >= before.length - suffix) return offset + (after.length - before.length);
  return after.length - suffix;
}

/**
 * The app sent `range` (or no selection): the range the editor moves to now,
 * when it is focused; otherwise null, the range kept for `takePendingSelection`
 * when the app focuses the editor. Nothing for the selection the editor
 * itself reported, coming back, unless `force` (the text changed from
 * outside under it, so the same numbers mean a move).
 */
export function selectionToApply(el: HTMLElement, range: TextSelectionRange | undefined, force = false): TextSelectionRange | null {
  if (!range || (!force && reported.get(el) === key(range))) return null;
  reported.set(el, key(range));
  if (el.ownerDocument.activeElement === el) return range;
  pending.set(el, range);
  return null;
}

/** The selection the app set while the editor was not focused, taken as the app focuses it. */
export function takePendingSelection(el: HTMLElement): TextSelectionRange | undefined {
  const range = pending.get(el);
  pending.delete(el);
  return range;
}

/** The viewer's selection: whether it is new (not the one the app set or the editor last reported), and so to report. */
export function noteViewerSelection(el: HTMLElement, range: TextSelectionRange): boolean {
  if (reported.get(el) === key(range)) return false;
  reported.set(el, key(range));
  return true;
}

/** The viewer focused the editor: their caret, not one the app set before. */
export function viewerFocused(el: HTMLElement): void {
  pending.delete(el);
}

/** The editor lost focus: it has no caret, so the selection the app holds is one to move to again. */
export function editorBlurred(el: HTMLElement): void {
  reported.delete(el);
}
